import React, { useCallback, useContext, useMemo, useState } from "react";
import { Transaction } from "@common/types/transaction";
import { CATEGORIZE_ACTION } from "@common/requiredActions";
import classNames from "classnames";
import { Category } from "@common/types/category";
import { Entity } from "@common/types/entity";
import { useSingleBusinessEntity } from "@hooks/useSingleBusinessEntity";
import { useUncategorizedCategory } from "@hooks/useUncategorizedCategory";
import { CategoryCombobox } from "@components/transactions/TransactionSplitV2/CategoryCombobox";
import { SelectBusinessEntityModal } from "@components/entity/SelectBusinessEntityModal";
import { StandardModal } from "@design-system/Modal/Modal";
import { TransactionDetailsSection } from "./TransactionDetailsSection";
import { TransactionDetailsContext } from "./transactionDetails.context";
import styles from "./TransactionDetailsCategory.module.scss";

interface TransactionDetailsCategoryProps {
    transaction: Transaction;
    isLocked?: boolean;
}

export const TransactionDetailsCategory: React.FC<
    TransactionDetailsCategoryProps
> = ({ transaction, isLocked }) => {
    const { onUpdate } = useContext(TransactionDetailsContext);
    const singleBusinessEntity = useSingleBusinessEntity();
    const uncategorized = useUncategorizedCategory();

    const [switchingToBusinessCategory, setSwitchingToBusinessCategory] =
        useState<Category>();

    const hideBusinessSelection = useCallback(
        () => setSwitchingToBusinessCategory(undefined),
        [],
    );

    const isBusiness =
        "isBusiness" in transaction ? transaction.isBusiness : true;
    const inReview = "inReview" in transaction && transaction.inReview;

    const enableWarning =
        transaction.requiredActions.includes(CATEGORIZE_ACTION);

    const uncategorizedWarning = useMemo(
        () =>
            transaction.categoryId === uncategorized?.id &&
            isBusiness &&
            !inReview,
        [transaction.categoryId, isBusiness, inReview, uncategorized?.id],
    );

    const handleCategoryChange = useCallback(
        (selected: Category | undefined) => {
            if (!selected) {
                return;
            }

            if (!selected.isBusiness) {
                return onUpdate({ categoryId: selected.id });
            }
            if (isBusiness) {
                return onUpdate({ categoryId: selected.id });
            } else if (singleBusinessEntity) {
                return onUpdate({
                    categoryId: selected.id,
                    entityId: singleBusinessEntity.id,
                });
            } else {
                setSwitchingToBusinessCategory(selected);
            }
        },
        [onUpdate, singleBusinessEntity, isBusiness],
    );

    const onBusinessSelected = useCallback(
        async (entity: Entity) => {
            if (!switchingToBusinessCategory) {
                return;
            }
            await onUpdate({
                categoryId: switchingToBusinessCategory.id,
                entityId: entity.id,
            });
            setSwitchingToBusinessCategory(undefined);
        },
        [switchingToBusinessCategory, onUpdate],
    );

    return (
        <>
            <TransactionDetailsSection
                className={classNames(
                    "transaction-details__section--category",
                    {
                        "transaction-details__section--category--locked":
                            isLocked,
                    },
                )}
                addon={
                    <CategoryCombobox
                        selectedCategory={transaction.category}
                        onChange={handleCategoryChange}
                        disabled={isLocked}
                        placeholder={
                            enableWarning && uncategorizedWarning
                                ? "Uncategorized"
                                : "Select category"
                        }
                        hideArrow
                        triggerButtonProps={{
                            variant: "primary",
                            block: true,
                            className: styles.categoryTrigger,
                        }}
                    />
                }
            />
            <StandardModal
                show={!!switchingToBusinessCategory}
                onHide={hideBusinessSelection}
            >
                <SelectBusinessEntityModal
                    prompt="To assign a business category please select a business entity you'd like to move this transaction to:"
                    onCancel={hideBusinessSelection}
                    onSelected={onBusinessSelected}
                    selectPlaceholder="Select entity"
                />
            </StandardModal>
        </>
    );
};
